import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';

import { useChangePassword } from '../hooks/use-account-mutations';
import { passwordSchema, type PasswordValues } from '../schemas/account-schemas';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['currentPassword', 'newPassword']);
const DEFAULTS: PasswordValues = { currentPassword: '', newPassword: '', confirmPassword: '' };

/** Changing the password signs out every other session of this account. */
export function PasswordCard() {
  const { t } = useTranslation(['account', 'common']);
  const message = useErrorMessage();
  const change = useChangePassword();
  const form = useForm<PasswordValues>({
    resolver: useI18nResolver(passwordSchema),
    defaultValues: DEFAULTS,
  });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await change.mutateAsync({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      form.reset(DEFAULTS);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  const password = (name: keyof PasswordValues, autoComplete: string, hint?: string) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t(`account:password.${name}`)}</FormLabel>
          <FormControl>
            <Input type="password" autoComplete={autoComplete} {...field} />
          </FormControl>
          {hint ? <FormDescription>{hint}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <FormSection
      id={SECTION_ANCHORS.password}
      tour="account.password"
      title={t('account:password.title')}
      description={t('account:password.description')}
    >
      <Form {...form}>
        <form
          onSubmit={submit}
          noValidate
          className="flex flex-col gap-4"
          aria-label={t('account:password.title')}
        >
          {form.formState.errors.root?.server?.message ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
            </Alert>
          ) : null}
          {password('currentPassword', 'current-password')}
          <div className="grid gap-4 sm:grid-cols-2">
            {password('newPassword', 'new-password', t('account:password.newPasswordHint'))}
            {password('confirmPassword', 'new-password')}
          </div>
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={change.isPending}>
              {change.isPending ? t('common:actions.saving') : t('account:password.submit')}
            </Button>
          </div>
        </form>
      </Form>
    </FormSection>
  );
}
